import { useState } from 'react';
import { Card, CardContent, CardHeader } from '../../../components/ui/Card';
import { Button } from '../../../components/ui/Button';
import { StatusBadge } from '../../../components/ui/StatusBadge';

import { DataView } from '../../../components/ui/DataView';
import { useClosureStatus, useRunChecklist, useApproveClosure, getChecklistLabel, useRegulatorySubmissions } from '../network/closure.api';
import type { ClosureStatus as ClosureStatusType, SchemeClosureRecord } from '../network/closure.api';

interface SchemeClosureTabProps {
  schemeId: string;
  schemeStatus: string;
}

function closureStatusVariant(status: ClosureStatusType): 'success' | 'warning' | 'info' | 'danger' {
  switch (status) {
    case 'CLOSED': return 'success';
    case 'CHECKLIST_PASSED': return 'info';
    case 'CHECKLIST_PENDING': return 'warning';
    case 'INITIATED': return 'warning';
    default: return 'info';
  }
}

function closureStatusLabel(status: ClosureStatusType): string {
  switch (status) {
    case 'INITIATED': return 'Initiated';
    case 'CHECKLIST_PENDING': return 'Checklist Pending';
    case 'CHECKLIST_PASSED': return 'Checklist Passed';
    case 'CLOSED': return 'Closed';
    default: return status;
  }
}

export function SchemeClosureTab({ schemeId, schemeStatus }: SchemeClosureTabProps) {
  const [actionError, setActionError] = useState<string | null>(null);
  const closureQuery = useClosureStatus(schemeId);
  const regulatoryQuery = useRegulatorySubmissions(schemeId);
  const runChecklist = useRunChecklist(schemeId);
  const approveClosure = useApproveClosure(schemeId);

  const handleRunChecklist = async () => {
    setActionError(null);
    try {
      await runChecklist.mutateAsync();
    // allow-silent-catch: error is surfaced to the user via the rendered `error` banner, not console/logger
    } catch (err: unknown) {
      setActionError(err instanceof Error ? err.message : 'Failed to run checklist');
    }
  };

  const handleApprove = async () => {
    setActionError(null);
    try {
      await approveClosure.mutateAsync();
    // allow-silent-catch: error is surfaced to the user via the rendered `error` banner, not console/logger
    } catch (err: unknown) {
      setActionError(err instanceof Error ? err.message : 'Failed to approve closure');
    }
  };

  const error = actionError || (closureQuery.error as Error | null)?.message;

  if (schemeStatus !== 'CLOSING' && schemeStatus !== 'CLOSED' && schemeStatus !== 'COMPLETED') {
    return (
      <Card>
        <CardContent className="p-8 text-center">
          <p className="text-sm text-text-secondary">
            Closure is only available when the scheme reaches <span className="font-semibold text-text-primary">COMPLETED</span> or <span className="font-semibold text-text-primary">CLOSING</span> status.
          </p>
          <p className="text-xs text-text-muted mt-1">Current scheme status: {schemeStatus}</p>
        </CardContent>
      </Card>
    );
  }

  if (schemeStatus === 'CLOSED') {
    return (
      <DataView query={closureQuery}>
        {(closure) => (
          <div className="space-y-6">
            <div className="bg-success-bg border border-success-500 rounded-[6px] p-5">
              <p className="font-semibold text-success-text text-sm">Scheme is Closed</p>
              {closure.closedAt && (
                <p className="text-xs text-success-text/80 mt-1">
                  Closed on {new Date(closure.closedAt).toLocaleString('en-IN')}
                </p>
              )}
              {closure.approvedBy && (
                <p className="text-xs text-success-text/80 mt-1">
                  Approved by {closure.approvedBy.slice(0, 8)}…
                </p>
              )}
              {closure.closureCertificateRef && (
                <a
                  href={closure.closureCertificateRef}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-block mt-3 text-xs font-medium text-accent-700 hover:underline bg-white px-3 py-1.5 rounded-[6px] border border-success-500"
                >
                  Download Certificate
                </a>
              )}
            </div>
            <ChecklistResults closure={closure} />
          </div>
        )}
      </DataView>
    );
  }

  return (
    <DataView query={closureQuery}>
      {(closure) => (
        <div className="space-y-6">
          {error && (
            <div className="p-3 bg-danger-bg border border-danger-500 rounded-[6px] text-danger-text text-sm">
              {error}
            </div>
          )}

          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <span className="text-sm font-semibold text-text-secondary uppercase tracking-wider">Closure Status</span>
              <StatusBadge
                status={closureStatusVariant(closure.status)}
                label={closureStatusLabel(closure.status)}
              />
            </div>
            <div className="flex gap-3">
              <Button
                variant="secondary"
                onClick={handleRunChecklist}
                loading={runChecklist.isPending}
                disabled={closure.status === 'CLOSED'}
              >
                Run Checklist
              </Button>
              {closure.status === 'CHECKLIST_PASSED' && (
                <Button
                  variant="primary"
                  onClick={handleApprove}
                  loading={approveClosure.isPending}
                  className="bg-success-500 hover:bg-success-600 text-white"
                >
                  Approve Closure
                </Button>
              )}
            </div>
          </div>

          {Object.keys(closure.checklistResults || {}).length > 0 && (
            <ChecklistResults closure={closure} />
          )}

          <RegulatoryFilingStatus query={regulatoryQuery} />

          {(!closure.checklistResults || Object.keys(closure.checklistResults).length === 0) && (
            <Card>
              <CardContent className="p-8 text-center">
                <p className="text-sm text-text-muted">
                  Click <span className="font-semibold">Run Checklist</span> to verify all closure conditions.
                </p>
              </CardContent>
            </Card>
          )}
        </div>
      )}
    </DataView>
  );
}

function ChecklistResults({ closure }: { closure: SchemeClosureRecord }) {
  const results = closure.checklistResults || {};
  const entries = Object.entries(results);

  if (entries.length === 0) return null;

  return (
    <Card>
      <CardContent className="p-0">
        <table className="w-full border-collapse text-left text-sm">
          <thead className="bg-surface-2 text-text-secondary font-semibold uppercase tracking-wider text-xs border-b border-border">
            <tr>
              <th className="p-4">Check</th>
              <th className="p-4">Item</th>
              <th className="p-4">Status</th>
              <th className="p-4">Checked At</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border text-text-primary">
            {entries.map(([code, item]) => (
              <tr key={code} className="hover:bg-surface-2 transition-colors">
                <td className="p-4 font-mono text-xs text-text-secondary">{code}</td>
                <td className="p-4 text-sm">{getChecklistLabel(code)}</td>
                <td className="p-4">
                  {item.passed ? (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-success-bg text-success-text rounded-full text-xs font-medium">Passed</span>
                  ) : (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-danger-bg text-danger-text rounded-full text-xs font-medium">Failed</span>
                  )}
                </td>
                <td className="p-4 text-sm text-text-secondary">
                  {item.checked_at ? new Date(item.checked_at).toLocaleString('en-IN') : '—'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </CardContent>
    </Card>
  );
}

function RegulatoryFilingStatus({ query }: { query: ReturnType<typeof useRegulatorySubmissions> }) {
  return (
    <DataView query={query}>
      {(submissions) => {
        if (submissions.length === 0) {
          return null;
        }

        const latest = submissions[0]; // Assuming they're ordered or we just care about the most recent

        return (
          <Card>
            <CardHeader>
              <h3 className="text-sm font-semibold text-text-primary">Regulatory Submission</h3>
            </CardHeader>
            <CardContent>
              <div className="flex flex-col gap-2 text-sm">
                <div className="flex justify-between border-b border-border pb-2">
                  <span className="text-text-secondary">Type</span>
                  <span className="font-medium">{latest.filingType}</span>
                </div>
                <div className="flex justify-between border-b border-border pb-2">
                  <span className="text-text-secondary">Status</span>
                  <StatusBadge 
                    status={latest.status === 'ACKNOWLEDGED' ? 'success' : latest.status === 'REJECTED' ? 'danger' : 'warning'} 
                    label={latest.status} 
                  />
                </div>
                {latest.referenceNumber && (
                  <div className="flex justify-between border-b border-border pb-2">
                    <span className="text-text-secondary">Reference #</span>
                    <span className="font-medium">{latest.referenceNumber}</span>
                  </div>
                )}
                {latest.submittedAt && (
                  <div className="flex justify-between border-b border-border pb-2">
                    <span className="text-text-secondary">Submitted At</span>
                    <span>{new Date(latest.submittedAt).toLocaleString('en-IN')}</span>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        );
      }}
    </DataView>
  );
}
